import React, { Fragment, useState, useContext, useEffect, useCallback } from 'react';
import { Card, useAPI, Table, AuthContext } from 'components/lib';
import { Dropdown } from 'primereact/dropdown';
import axios from 'axios';
import apps from '../../constants/apps.json';
import { NewAccountForm } from './NewAccountForm';
import { NewAccountWizard } from './NewAccountWizard';
import { UnifiedClientWizard } from './UnifiedClientWizard';
import styles from './OrganizationList.module.css';
import Snackbar from '@mui/material/Snackbar';
import Modal from '@mui/material/Modal';
import { debugLog } from '../../utils/debugLog';

export function OrganizationList(props){

  const records = useAPI('/api/user/associatedDataConnections');
  const authContext = useContext(AuthContext);
  
  const [recordList, setRecordList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false); // State to manage modal visibility
  const [editData, setEditData] = useState(null); // State to manage edit data
  const [filter, setFilter] = useState({ active: true}); // State to manage filter - default to active only
  const [clientSaved, setClientSaved] = useState(null); // State to manage client saved notification
  const [isUnifiedWizardOpen, setIsUnifiedWizardOpen] = useState(false); // State to manage UnifiedClientWizard modal visibility

  // Helper functions for terminology based on firm type
  const isAccountingFirm = authContext.user?.is_firm;
  const entityTerm = isAccountingFirm ? 'Client' : 'Company';
  const entityTermLower = isAccountingFirm ? 'client' : 'company';
  const entityTermPlural = isAccountingFirm ? 'Clients' : 'Companies';

  // Check for bulk connection completion
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const bulkComplete = urlParams.get('bulkComplete') === 'true';
    
    if (bulkComplete) {
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Check if there's a bulk state to continue
      const bulkState = sessionStorage.getItem('bulkConnectionState');
      if (bulkState) {
        try {
          const state = JSON.parse(bulkState);
          
          // Mark the current connection as completed and advance
          const updatedCompleted = [...state.completed];
          const currentClient = state.queue[state.currentIndex];
          if (currentClient) {
            updatedCompleted.push(currentClient);
          }
          
          const nextIndex = state.currentIndex + 1;
          
          // Update the state
          const updatedState = {
            ...state,
            currentIndex: nextIndex,
            completed: updatedCompleted
          };
          
          sessionStorage.setItem('bulkConnectionState', JSON.stringify(updatedState));
          
          // If there are more connections, reopen the unified wizard
          if (nextIndex < state.queue.length) {
            setIsUnifiedWizardOpen(true);
          } else {
            // All done! Clean up and show completion
            sessionStorage.removeItem('bulkConnectionState');
            alert(`🎉 Bulk connection complete! Successfully connected ${updatedCompleted.length} ${entityTermPlural.toLowerCase()}.`);
          }
          
          // Refresh the organization list
          refreshOrganizationList();
          
        } catch (error) {
          console.error('Error processing bulk completion:', error);
          sessionStorage.removeItem('bulkConnectionState');
        }
      }
    }
  }, [entityTermPlural]);


  const getAppIcon = useCallback((shortName) => {
    const app = apps.find(app => app.short_name === shortName);
    return app ? app.icon : '';
  }, []);

  const formatHealthScore = useCallback((score, delta) => {
    if (score === 'na' || score === '') return score;
    
    if (delta !== null && delta !== undefined) {
      let display, color, tooltipText;
      
      if (delta > 0) {
        display = `↑ ${delta}`;
        color = '#4CAF50';
        tooltipText = `Health score improved by ${delta} points in the last 14 days`;
      } else if (delta < 0) {
        display = `↓ ${Math.abs(delta)}`;
        color = '#f44336';
        tooltipText = `Health score declined by ${Math.abs(delta)} points in the last 14 days`;
      } else {
        display = ' — ';
        color = '#757575';
        tooltipText = 'Health score unchanged in the last 14 days';
      }
      
      return (
        <span title={tooltipText} style={{ cursor: 'help' }}>
          {score}
          <span 
            style={{ 
              color, 
              fontSize: '0.85em', 
              fontWeight: '500', 
              marginLeft: '8px',
              fontStyle: delta === 0 ? 'italic' : 'normal'
            }}
          >
            {display}
          </span>
        </span>
      );
    }
    return score;
  }, []);

  

  const action = (
    <button onClick={() => authContext.switchAccount(clientSaved)}>Setup Data</button>
  );

  const updateRecordList = useCallback((records) => {
    if(records?.length) {
      let recList = [];

      records.forEach((item) => {
        
        recList.push({
          id: item.id,
          account: item.org_Name,
          client: item.org_Name,
          monitoring_level: item.account_type === 'full_monitoring' ? 'Full Monitoring' : 'Health Score Only',
          app: getAppIcon(item.app), // Compute app icon based on short_name
          data_Source: item.name || 'Add Data Source', // Default to 'Add Data Source' if no data source
          health_Score: item.name ? item.health_Score : '',
          health_Score_display: item.name ? formatHealthScore(item.health_Score, item.health_Score_delta) : '',
          health_Score_delta: item.health_Score_delta,
          records_to_Clean: item.name ? item.records_to_Clean : '',
          plan: item.plan,
          last_Updated: item.updated_at,
          active: item.active,
          checked: false,
          app_conn_id: item.app_conn_id,
        });
      });

      setRecordList(recList);

    }
    else {
      setRecordList([]);
    }
  }, [getAppIcon, formatHealthScore]);

  useEffect(() => {
    if (records.data) {
      updateRecordList(records.data);
    }
  }, [records.data, updateRecordList]);

  const createNewAccount = () => {
    setEditData(null); // Clear edit data
    setIsModalOpen(true); // Open the modal
  };

  const saveNewAccount = async (accountData) => {
    try {
      // If using the wizard, the account is already created
      if (accountData.id) {
        setIsModalOpen(false); // Close the modal
        // Refresh the organization list table
        const updatedRecords = await axios.get('/api/user/associatedDataConnections');
        updateRecordList(updatedRecords.data.data);
        setClientSaved(accountData.id); // Show client saved notification
      } else {
        // Legacy form submission
        const response = await axios.post('/api/account', accountData);
        debugLog('[OrganizationList] - New client account created:', response.data);
        setIsModalOpen(false); // Close the modal
        // Refresh the organization list table
        const updatedRecords = await axios.get('/api/user/associatedDataConnections');
        updateRecordList(updatedRecords.data.data);
        setClientSaved(response.data.account.id); // Show client saved notification
      }
    } catch (error) {
      console.error('Failed to create new client account', error);
    }
  };

  const updateAccount = async (data) => {
    debugLog('[OrganizationList] - Updating account:', data);
    try {
      const response = await axios.patch(`/api/account/${data.id}`, data);
      debugLog('[OrganizationList] - Account updated:', response.data);
      setIsModalOpen(false); // Close the modal
      // Refresh the organization list table
      const updatedRecords = await axios.get('/api/user/associatedDataConnections');
      updateRecordList(updatedRecords.data.data);
      setClientSaved(response.data.account.id); // Show client saved notification
    } catch (error) {
      console.error('Failed to update account', error);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false); // Close the modal
  };

  const openUnifiedWizard = () => {
    setIsUnifiedWizardOpen(true); // Open the UnifiedClientWizard modal
  };

  const closeUnifiedWizard = () => {
    setIsUnifiedWizardOpen(false); // Close the UnifiedClientWizard modal
  };

  const handleUnifiedWizardComplete = (createdClients) => {
    setIsUnifiedWizardOpen(false);
    // Refresh the organization list to show new clients
    refreshOrganizationList();
    // Show success notification
    if (createdClients.length === 1) {
      setClientSaved(createdClients[0].id);
    } else {
      // For multiple clients, we could show a different notification
      alert(`Successfully created ${createdClients.length} ${entityTermPlural.toLowerCase()}!`);
    }
  };

  const refreshOrganizationList = async () => {
    const updatedRecords = await axios.get('/api/user/associatedDataConnections');
    updateRecordList(updatedRecords.data.data);
  };

  const loadAccount = (rec, index) => {
    authContext.switchAccount(rec.id, '/account');
    //History.push('/dashboard');
  };

  const switchApp = (rec, index) => {
    // console.log('Switching app to:', rec);
    
    // Check if client has a data connection
    if (rec.data_Source === 'Add Data Source' || !rec.app_conn_id) {
      // No data connection, redirect to connections screen
      authContext.switchAccount(rec.id, '/client/connections');
    } else {
      // Has data connection, go to dashboard
      authContext.switchApp(rec.app_conn_id, "/dashboard", rec.id);
    }
  };


  // function importClients(data, callback){
  //   viewContext.modal.show({
  //     title: 'Import Clients from Karbon Practice Management',
  //     text: 'Enter your Karbon API Access Key below and your active organization clients will be automatically connected to LedgerView.',
  //     form: {
  //       access_key: {
  //           label: 'Access Key',
  //           type: 'text',
  //           required: 'true',
  //           default: '',
  //         },
  //     },
  //     buttonText: 'Import',
  //     url: '/api/auditRule/',
  //     method: 'POST'

  //   }, (res) => {

  //     viewContext.notification.show('Client import has started', 'success', true);
  //     callback(res);

  //   });
  // }


  function editAccount(data){
    window.location.href = `/account/${data.id}`;
  }


  const filterRecords = (record) => {
    // Show all records if filter is null/undefined
    if (filter.active === null || filter.active === undefined) {
      return true;
    }
    // Filter by active status - handle cases where record.active might be undefined
    const recordActive = record.active !== false; // Default to true if undefined
    return recordActive === filter.active;
  };

  const filteredRecordList = recordList.filter(filterRecords);
  
  // Debug logging
  // console.log('Current filter:', filter);
  // console.log('Total records:', recordList.length);
  // console.log('Filtered records:', filteredRecordList.length);

  const handleFilterChange = (e) => {
    setFilter({ ...filter, active: e.value });
  };

  // Check if user has any clients/data sources
  const hasClients = recordList.length > 0;
  const hasActiveDataSources = recordList.filter(record => record.data_Source !== 'Add Data Source' && record.active).length > 0;

  // Show loading screen while waiting for data
  if (records.loading) {
    return (
      <Fragment>
        <div className={styles.loadingContainer}>
          <Card>
            <div className={styles.loadingContent}>
              <div className={styles.loadingSpinner}></div>
              <h2>Loading LedgerView...</h2>
              <p>Please wait while we load your {entityTermLower} information.</p>
            </div>
          </Card>
        </div>
      </Fragment>
    );
  }

  return(
    <Fragment>
      <Snackbar open={clientSaved !== null} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={5000} message={`${entityTerm} info saved.`} action={action} />

      {!hasClients ? (
        // Welcome/Onboarding View for New Users
        <div className={styles.welcomeContainer}>
          <Card>
            <div className={styles.welcomeContent}>
              <div className={styles.welcomeHeader}>
                <h1>Welcome to LedgerView! 👋</h1>
                <p className={styles.welcomeSubtitle}>
                  Get started by adding your first {entityTermLower} to begin analyzing their financial data
                </p>
              </div>

              <div className={styles.gettingStartedSteps}>
                <h3>Getting Started is Easy:</h3>
                <div className={styles.stepsContainer}>
                  <div className={styles.step}>
                    <div className={styles.stepNumber}>1</div>
                    <div className={styles.stepContent}>
                      <h4>Add Your First {entityTerm}</h4>
                      <p>Create a {entityTermLower} profile to organize their financial data</p>
                    </div>
                  </div>
                  <div className={styles.step}>
                    <div className={styles.stepNumber}>2</div>
                    <div className={styles.stepContent}>
                      <h4>Connect Data Source</h4>
                      <p>Link QuickBooks, Xero, or import files to start analysis</p>
                    </div>
                  </div>
                  <div className={styles.step}>
                    <div className={styles.stepNumber}>3</div>
                    <div className={styles.stepContent}>
                      <h4>Review Insights</h4>
                      <p>Get instant health scores and cleanup recommendations</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.welcomeActions}>
                <button className={styles.primaryCtaButton} onClick={createNewAccount}>
                  <span>🚀</span> Add Your First {entityTerm}
                </button>
                {isAccountingFirm && (
                  <button className={styles.secondaryCtaButton} onClick={openUnifiedWizard}>
                    <span>⚡</span> Add Multiple {entityTermPlural}
                  </button>
                )}
              </div>

              <div className={styles.helpSection}>
                <p className={styles.helpText}>
                  <strong>Need help?</strong> Check out our <button className={styles.helpLink} onClick={() => {}}>quick start guide</button> or 
                  <button className={styles.helpLink} onClick={() => {}}> watch a 2-minute demo</button>
                </p>
              </div>
            </div>
          </Card>
        </div>
      ) : (
        // Existing Client List View
        <Card>
          <>
            <div className={styles.clientListHeader}>
              <div className={styles.headerContent}>
                <h2>Your {entityTermPlural}</h2>
                <p className={styles.headerSubtitle}>
                  Manage {entityTermLower} accounts and monitor data health across all connections
                </p>
              </div>
              
              <div className={styles.headerActions}>
                <button className={`${styles.actionButton} ${styles.primary}`} onClick={createNewAccount}>
                  <span>+</span> Add {entityTerm}
                </button>
                {isAccountingFirm && (
                  <button className={`${styles.actionButton} ${styles.secondary}`} onClick={openUnifiedWizard}>
                    ⚡ Add Multiple
                  </button>
                )}
                
                <Dropdown 
                  value={{ label: filter.active === true ? 'Active Only' : filter.active === false ? 'Inactive Only' : 'All', value: filter.active }}
                  options={[
                    { label: 'Active Only', value: true }, 
                    { label: 'Inactive Only', value: false }
                  ]} 
                  onChange={handleFilterChange} 
                  placeholder="Filter by Status" 
                  className={styles.filterDropdown} 
                  style={{ minWidth: '120px', fontSize: '14px' }}
                />
              </div>
            </div>

            <div className={styles.statsFooter}>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>{filteredRecordList.length}</span>
                <span className={styles.statLabel}>
                  {filter.active === true ? 'Active' : 
                   filter.active === false ? 'Inactive' : 'Total'} {entityTermPlural}
                </span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>{filteredRecordList.filter(record => record.data_Source !== 'Add Data Source' && record.active).length}</span>
                <span className={styles.statLabel}>Active Data Sources</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>{filteredRecordList.filter(record => record.health_Score && record.health_Score > 80 && record.active).length}</span>
                <span className={styles.statLabel}>Healthy {entityTermPlural}</span>
              </div>
            </div>

            {!hasActiveDataSources && (
              <div className={styles.noDataBanner}>
                <div className={styles.bannerContent}>
                  <h4>⚡ Ready to connect data sources?</h4>
                  <p>Click on any {entityTermLower} below to connect their QuickBooks, Xero, or other apps to start getting insights.</p>
                </div>
              </div>
            )}

            <Table
              data={filteredRecordList.map(record => ({
                ...record,
                health_Score: {
                  value: record.health_Score === 'na' ? -1 : record.health_Score,
                  display: record.health_Score_display
                }
              }))}
              header={[
                { name: 'app', title: 'App', sort: true },
                { name: 'data_Source', title: 'Data Source', sort: true },
                { name: 'client', title: entityTerm, sort: true },
                { name: 'monitoring_level', title: 'Monitoring Level', sort: true },
                { name: 'health_Score', title: 'Health Score', sort: true },
                { name: 'records_to_Clean', title: 'Records to Clean', sort: true },
                { name: 'last_Updated', title: 'Last Updated', sort: true }
              ]}
              search={recordList.length > 10}
              sort={true}
              show={['app', 'data_Source', authContext.user?.is_firm ? 'client' : 'client', 'monitoring_level', 'health_Score', 'records_to_Clean', 'last_Updated']} 
              hyperlink={[
                {
                  col: 'account',
                  url: '',
                  callback: loadAccount
                },
                {
                  col: 'client',
                  url: '',
                  callback: switchApp
                }
              ]}
              actions={{ 
                hyperlink: switchApp,
                edit: editAccount,
              }}
            />
            
            <div className={styles.bottomActions}>
              <button className={`${styles.actionButton} ${styles.primary}`} onClick={createNewAccount}>
                <span>+</span> Add {entityTerm}
              </button>
            </div>
          </>
        </Card>
      )}

      <Modal open={isModalOpen} onClose={closeModal}>
        <div className={styles.modalContent} style={{ width: '70%', maxWidth: '800px', margin: 'auto', marginTop: '3%', padding: '20px', backgroundColor: '#fff', boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)', borderRadius: '8px' }}>
          {editData ? (
            <NewAccountForm onSave={updateAccount} onCancel={closeModal} editData={editData} entityTerm={entityTerm} />
          ) : (
            <NewAccountWizard onSave={saveNewAccount} onCancel={closeModal} entityTerm={entityTerm} />
          )}
        </div>
      </Modal>
      
      <Modal open={isUnifiedWizardOpen} onClose={closeUnifiedWizard}>
        <div className={styles.modalContent} style={{ width: '90%', maxWidth: '1100px', margin: 'auto', marginTop: '2%', padding: '20px', backgroundColor: '#fff', boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)', borderRadius: '8px' }}>
          <UnifiedClientWizard onClose={closeUnifiedWizard} onComplete={handleUnifiedWizardComplete} entityTerm={entityTerm} />
        </div>
      </Modal>
    </Fragment>
  );
}
